import { ConfigurationError, type GenericError } from '@cian/peperrors/shared';

import { type AssetName, EAssetContentType, EAssetType } from '../../../shared/assets/index.js';
import { Deferred } from '../../../shared/utils/deferred/index.js';

import type { IBundleManifest, IImageManifest } from '../../../shared/manifest/index.js';
import type { AssetsStorage } from '../assets/AssetsStorage.js';
import type { FileAsset, InlineAsset, ModuleAsset } from '../assets/assets.js';

export enum EBundleStatus {
  Initial,
  Loading,
  Loaded,
  Failed,
}

export class Bundle<Exports = unknown> implements IBundleManifest {
  public readonly name: string;

  private readonly manifest: IBundleManifest;
  private readonly bundleAssets: BundleAssets;
  private _status: EBundleStatus = EBundleStatus.Initial;
  private deferred: Deferred<Exports>;

  public constructor(name: string, manifest: IBundleManifest, assets: BundleAssets) {
    this.manifest = manifest;
    this.name = name;
    this.bundleAssets = assets;
  }

  public get entry() {
    return this.manifest.entry;
  }

  public get globals() {
    return this.manifest.globals;
  }

  public get assets() {
    return this.manifest.assets;
  }

  public get status() {
    return this._status;
  }

  // TODO: Отмена загрузки. В SPA приложении пользователь может перейти на предыдущую страницу
  /**
   * Выполняет загрузку всех ресурсов бандле и возвращает экспорты из entry
   * @returns {Promise<Exports>}
   */
  public async load(): Promise<Exports> {
    if (this._status === EBundleStatus.Loaded) {
      return (this.getAsset<Exports>(this.manifest.entry, EAssetContentType.Script) as ModuleAsset<Exports>)
        .exports as Exports;
    }

    if (this._status === EBundleStatus.Loading) {
      return this.deferred;
    }

    this._status = EBundleStatus.Loading;
    this.deferred = new Deferred<Exports>();

    try {
      const sources: Array<Promise<void>> = [];

      if (this.manifest.globals) {
        // for (const asset of this.manifest.globals) {
        // TODO: Могут быть зависимости, которые должны блокировать загрузку бандла
        // sources.push((this.getAsset(asset) as FileAsset).load());
        // }
      }

      // TODO: Загрузка основного файла стилей
      const entry = this.getAsset<Exports>(this.manifest.entry, EAssetContentType.Script) as ModuleAsset<Exports>;

      sources.push(entry.load());

      for (const assetName of this.manifest.assets || []) {
        const asset = this.getAsset(assetName);
        if (asset.type === EAssetType.File) {
          sources.push(asset.load());
        } else {
          asset.load();
        }
      }

      // TODO: Каждый ресурс должен быть обработан по отдельности
      await Promise.all(sources);

      this._status = EBundleStatus.Loaded;

      this.deferred.resolve(entry.exports as Exports);

      return entry.exports as Exports;
    } catch (error) {
      this._status = EBundleStatus.Failed;

      if (error instanceof CustomEvent) {
        this.deferred.reject(error.detail);

        throw error.detail as GenericError;
      }

      this.deferred.reject(error);

      throw error;
    }
  }

  private getAsset<Exports = unknown>(name: string, contentType?: EAssetContentType) {
    const asset = this.bundleAssets.getAsset<Exports>(name);

    if (!asset) {
      throw new ConfigurationError({
        message: `Asset ${name} of bundle ${this.name} not found in manifest`,
        domain: 'mf-registry.bundle.getAsset',
      });
    }

    if (contentType && contentType !== asset.contentType) {
      throw new ConfigurationError({
        message: `Asset ${name} of bundle ${this.name} has invalid content type "${asset.contentType}"`,
        domain: 'mf-registry.bundle.getAsset',
      });
    }

    return asset;
  }
}

export class BundleAssets {
  private readonly assets: IImageManifest['assets'];
  private readonly assetsStorage: AssetsStorage;
  private readonly assetsMap: Map<AssetName, ModuleAsset | InlineAsset | FileAsset> = new Map();

  public constructor(assets: IImageManifest['assets'], assetsStorage: AssetsStorage) {
    this.assets = assets;
    this.assetsStorage = assetsStorage;

    this.addAssets();
  }

  public getAsset<Exports = unknown>(name: string): ModuleAsset<Exports> | FileAsset | InlineAsset | undefined {
    return this.assetsMap.get(name) as ModuleAsset<Exports> | FileAsset | InlineAsset | undefined;
  }

  private addAssets() {
    for (const [name, manifest] of Object.entries(this.assets)) {
      const asset = this.assetsStorage.createAssetByManifest(manifest);

      this.assetsMap.set(name, asset);
    }
  }
}
